import React, {
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import classNames from 'classnames';
import { durationString } from '../../../../utils/utils';
import Icon, { COLORS, ICONS } from '../../../general-components/icon';
import TimeTrack from './time-track';
import Playhead from './playhead';
import TrimDragBox from './drag-box';
import { TrimTime } from './video-editor';
import { TRIM_MEDIA_CONTROLS } from '../../../../types/constants';

interface Props {
	totalSeconds: number;
	elapsedSeconds: number;
	thumbnails: Array<string>;
	isPlaying: boolean;
	trimTimes: TrimTime;
	trimTimeChange: Function;
	changeElapsedSeconds: Function;
	handleSkipBack: () => void;
	handleRewind: () => void;
	handlePlay: () => void;
	handleFastForward: () => void;
	handleSkipForward: () => void;
}

// interval - the number of seconds between each timestamp
// interval = secondIntervals(zoomLevel);
// gives the interval value in seconds for each zoom level
export const secondsIntervals = [1, 2, 5, 10, 30, 60, 120, 300, 600];

// corresponding $width-constant in _video-editor.scss
export const WIDTH_CONSTANT = 100;

export const initialZoom = (seconds: number, windowWidth: number): number => {
	let initZoom = secondsIntervals.length - 1;
	let i = 0;
	for (i; i < secondsIntervals.length; i += 1) {
		const timeStampsAtThisInterval = seconds / secondsIntervals[i];
		if (timeStampsAtThisInterval * WIDTH_CONSTANT < windowWidth) {
			// start at small intervals, so 2s - will overflow
			// break when we find the first one that doesn't overflow
			initZoom = i;
			break;
		}
	}
	return initZoom;
};

export default function TrimTimeline({
	totalSeconds,
	elapsedSeconds,
	thumbnails,
	isPlaying,
	trimTimes,
	trimTimeChange,
	changeElapsedSeconds,
	handleSkipBack,
	handleRewind,
	handlePlay,
	handleFastForward,
	handleSkipForward,
}: Props) {
	const [zoomInitialized, setZoomInitialized] = useState(false);
	const [zoomLevel, setZoomLevel] = useState(initialZoom(totalSeconds, window.innerWidth));

	useEffect(() => {
		setZoomLevel(initialZoom(totalSeconds, window.innerWidth));
		setTimeout(() => {
			setZoomInitialized(true);
		}, 150);
	}, [totalSeconds]);

	const handleZoomIn = () => {
		if (zoomLevel > 0) {
			setZoomLevel(zoomLevel - 1);
		}
	};

	const handleZoomOut = () => {
		if (zoomLevel < secondsIntervals.length - 1) {
			setZoomLevel(zoomLevel + 1);
		}
	};

	const thumbTrackWidth = useMemo<number>(() => {
		const width = WIDTH_CONSTANT * (totalSeconds / secondsIntervals[zoomLevel]);
		return width;
	}, [totalSeconds, secondsIntervals, zoomLevel]);

	const backgroundStyle = useMemo(() => {
		const thumbWidth = WIDTH_CONSTANT * (totalSeconds / secondsIntervals[zoomLevel]);
		return {
			backgroundImage: `url('${thumbnails[0]}')`,
			width: thumbWidth,
		};
	}, [thumbnails, totalSeconds, zoomLevel]);

	const wrapRef = useRef<HTMLDivElement | null>(null);
	const thumbTrackRef = useRef<HTMLDivElement | null>(null);
	const [scrollEnabled, setScrollEnabled] = useState(false);
	const [minTimestamps, setMinTimestamps] = useState(1);

	const calcScrollAndMinTimestamps = () => {
		if (thumbTrackRef.current && wrapRef.current) {
			if (thumbTrackRef.current.clientWidth + 40 > wrapRef.current.clientWidth) {
				setScrollEnabled(true);
			} else {
				setMinTimestamps(window.innerWidth / WIDTH_CONSTANT);
				setScrollEnabled(false);
				wrapRef.current.scrollTo({ left: 0 });
			}
		}
	};

	const handleTrimMediaControls = (type: string) => {
		if (type === TRIM_MEDIA_CONTROLS.START) {
			handleSkipBack();
		} else if (type === TRIM_MEDIA_CONTROLS.REWIND) {
			handleRewind();
		} else if (type === TRIM_MEDIA_CONTROLS.FORWARD) {
			handleFastForward();
		} else {
			handleSkipForward();
		}

		const dragEl = document.getElementById('trim-drag-box');
		const playhead = document.getElementById('playhead');
		const scrollingRight = type === TRIM_MEDIA_CONTROLS.FORWARD || type === TRIM_MEDIA_CONTROLS.END;
		if (scrollEnabled && wrapRef.current && dragEl && playhead) {
			const dragLWidth = (dragEl.children[0] as HTMLElement).offsetWidth;
			const dragRWidth = (dragEl.children[dragEl.children.length - 1] as HTMLElement).offsetWidth;
			const dragBoxEnd = (dragEl.offsetLeft + dragEl.clientWidth + dragLWidth + dragRWidth);

			const skipDistance = ((10 / secondsIntervals[zoomLevel]) * WIDTH_CONSTANT);
			const curPlayheadPos = playhead.offsetLeft + 8;
			const playheadDragOffset = playhead.offsetWidth + dragRWidth;

			let newPlayheadPos: number;
			if (type === TRIM_MEDIA_CONTROLS.START) {
				newPlayheadPos = dragEl.offsetLeft;
			} else if (type === TRIM_MEDIA_CONTROLS.REWIND) {
				newPlayheadPos = curPlayheadPos - skipDistance;
			} else if (type === TRIM_MEDIA_CONTROLS.FORWARD) {
				newPlayheadPos = curPlayheadPos + skipDistance + playheadDragOffset;
			} else {
				newPlayheadPos = dragBoxEnd;
			}

			const maxLeft = wrapRef.current.scrollLeft;
			const maxRight = wrapRef.current.scrollLeft + wrapRef.current.clientWidth;

			if (newPlayheadPos <= maxLeft || newPlayheadPos >= maxRight) {
				wrapRef.current.scrollTo({
					left: scrollingRight
						? newPlayheadPos - wrapRef.current.clientWidth
						: newPlayheadPos,
				});
			}
		}
	};

	const wrapperObserver = new ResizeObserver(() => calcScrollAndMinTimestamps());
	const thumbTrackObserver = new ResizeObserver(() => calcScrollAndMinTimestamps());

	useEffect(() => {
		if (wrapRef.current && thumbTrackRef.current) {
			wrapperObserver.observe(wrapRef.current);
			thumbTrackObserver.observe(thumbTrackRef.current);
		}

		return () => {
			if (wrapRef.current && thumbTrackRef.current) {
				wrapperObserver.disconnect();
				thumbTrackObserver.disconnect();
			}
		};
	}, [wrapRef.current]);

	return (
		<div className='tt-wrap' style={{ opacity: zoomInitialized ? 1 : 0 }}>
			<div className='tt-header'>
				<div className='tth-left'>
					<span>Timeline</span>
				</div>
				<div className='tth-center'>
					<div className='tth-display-time'>{durationString(elapsedSeconds, true)}</div>
					<div className='tth-media-controls'>
						<div onClick={() => handleTrimMediaControls(TRIM_MEDIA_CONTROLS.START)} className='tth-btn tth-skip-back'>
							<Icon name={ICONS.SKIP_ARROWS} size={18} color={COLORS.LIGHT_GRAY}/>
						</div>
						<div onClick={() => handleTrimMediaControls(TRIM_MEDIA_CONTROLS.REWIND)} className='tth-btn tth-rewind'>
							<Icon name={ICONS.FAST_FORWARD} size={18} color={COLORS.LIGHT_GRAY}/>
						</div>
						<div
							onClick={handlePlay}
							className={classNames('tth-btn tth-play', { playing: isPlaying })}
						>
							<Icon
								name={!isPlaying ? ICONS.PLAY_PADDING : ICONS.PAUSE}
								size={14}
								color={COLORS.LIGHT_GRAY}
							/>
						</div>
						<div onClick={() => handleTrimMediaControls(TRIM_MEDIA_CONTROLS.FORWARD)} className='tth-btn'>
							<Icon name={ICONS.FAST_FORWARD} size={18} color={COLORS.LIGHT_GRAY}/>
						</div>
						<div onClick={() => handleTrimMediaControls(TRIM_MEDIA_CONTROLS.END)} className='tth-btn'>
							<Icon name={ICONS.SKIP_ARROWS} size={18} color={COLORS.LIGHT_GRAY} />
						</div>
					</div>
					<div className='tth-display-time'>{durationString(trimTimes.end, true)}</div>
				</div>
				<div className='tth-right'>
					<div onClick={handleZoomOut} className='tth-btn'>
						<Icon name={ICONS.ZOOM_OUT} size={18} color={COLORS.LIGHT_GRAY} />
					</div>
					<div onClick={handleZoomIn} className='tth-btn tth-zoom-in'>
						<Icon name={ICONS.ZOOM_IN} size={18} color={COLORS.LIGHT_GRAY} />
					</div>
				</div>
			</div>
			<div
				className='position-relative tt-scrollable-wrap'
				style={{
					overflowX: scrollEnabled ? 'auto' : 'hidden',
					paddingBottom: scrollEnabled ? '0' : '16px',
				}}
				ref={wrapRef}
			>
				<Playhead
					totalSeconds={totalSeconds}
					elapsedSeconds={elapsedSeconds}
					changeElapsedSeconds={changeElapsedSeconds}
					thumbTrackWidth={thumbTrackWidth}
					trimTimes={trimTimes}
					secondsInterval={secondsIntervals[zoomLevel]}
				/>

				<TimeTrack
					totalSeconds={totalSeconds}
					secondsInterval={secondsIntervals[zoomLevel]}
					minTimestamps={minTimestamps}
				/>

				<div
					className='tt-thumbnails'
					style={backgroundStyle}
					ref={thumbTrackRef}
				/>

				<TrimDragBox
					trimTimes={trimTimes}
					secondsInterval={secondsIntervals[zoomLevel]}
					trimTimeChange={trimTimeChange}
					thumbTrackWidth={thumbTrackWidth}
					bgStyle={backgroundStyle}
				/>
				{scrollEnabled && <div className="scrollbar-divider" />}
			</div>
		</div>
	);
}
